/**
 * IndexedDB数据库结构定义
 * 定义了多维度笔记应用的数据库schema、表结构和索引
 */

import { BaseDocument,DocumentLink,DocumentType,LinkType,UserSettings } from '@/types'
import { logger } from '@utils/logger'
import { DBSchema } from 'idb'

/**
 * 数据库版本号
 * 每次schema变更时需要递增
 *
 * v2: 新增 sync_queue / sync_meta store，documents 记录附带 _etag / _syncStatus 运行时字段
 *     用于 UFS 远程同步（本地缓存 + 后台推送 + ETag 校验）
 * v3: 新增 tombstones store，记录已 permanentDelete 的 docId，防止 incrementalSync 重新拉回已删文档
 * v4: documents store 加 by-source 索引，支持按来源（native/plain）筛选 plain 文件
 * v5: documents store 加 by-ufs-parent-id 索引，desk 列目录改读 IndexedDB（UFS 文件树镜像）
 */
export const DATABASE_VERSION = 6

/**
 * 数据库名称
 */
export const DATABASE_NAME = 'multidimensional-notes-db'

export const DATABASE_STORE_NAMES = [
  'documents',
  'links',
  'tags',
  'search_index',
  'settings',
  'cache',
  'attachments',
  'sync_queue',
  'sync_meta',
  'tombstones',
] as const

export type DatabaseStoreName = (typeof DATABASE_STORE_NAMES)[number]

/**
 * 同步状态
 * - synced: 本地与 UFS 一致
 * - pending: 本地有变更，待推送到 UFS
 * - conflict: UFS 版本与本地不一致，需人工或策略合并
 * - error: 同步失败，待重试
 */
export type SyncStatus = 'synced' | 'pending' | 'conflict' | 'error'

/**
 * 文档记录类型
 * 在 BaseDocument 基础上附加 UFS 同步所需的运行时字段
 * 这些字段不进入业务层，documentDAO 对外返回时会剥离
 */
export interface DocumentRecord extends BaseDocument {
  /** UFS 文件 ETag，用于条件请求（If-None-Match） */
  _etag?: string
  /** UFS 文件 ID（通常等于 docId，但留作未来 docId 与 fileId 分离的可能） */
  _fileId?: string
  /** 同步状态 */
  _syncStatus?: SyncStatus
  /** 最近一次同步失败原因，用于排障 */
  _syncError?: string
  /**
   * UFS 侧记录的 updatedAt（上次同步成功时快照）
   * 用于冲突检测：本地 pending 时若远端 updatedAt 晚于本地 _remoteUpdatedAt，
   * 说明远端在本地上次同步后被其他端修改，需标记 conflict 而非覆盖。
   */
  _remoteUpdatedAt?: Date
}

/**
 * 同步队列条目
 * documentDAO 的每次写操作会入队一条，UfsSyncService 后台 flush 时消费
 */
export interface SyncQueueEntry {
  /** 条目 ID（uuid） */
  id: string
  /** 操作类型 */
  operation: 'create' | 'update' | 'delete'
  /** 关联文档 ID */
  docId: string
  /** create/update 时携带的文档快照；delete 时为空 */
  payload?: DocumentRecord
  /** 入队时间 */
  enqueuedAt: Date
  /** 已尝试次数，用于退避 */
  attempts: number
  /** 最近一次错误信息 */
  lastError?: string
  /** 强制覆盖远端（跳过 If-Match 校验），用于 resolveConflictKeepLocal */
  force?: boolean
}

/**
 * 同步元信息（key-value store）
 * 存储全局同步状态，如最近一次增量同步时间、bootstrap 是否完成
 */
export interface SyncMeta {
  key: string
  value: unknown
  updatedAt: Date
}

/**
 * 墓碑记录：永久删除的 docId，防止 incrementalSync 重新拉回
 * 发起端 tombstone 防自身复兴；他端靠 isDeleted=true 软删除信号软删
 */
export interface TombstoneRecord {
  /** 文档 ID（即 IndexedDB key） */
  docId: string
  /** 永久删除时间，用于未来 janitor 清理 TTL */
  deletedAt: Date
}

/**
 * 数据库Schema接口定义
 * 扩展idb的DBSchema接口，定义所有对象存储的结构
 */
export interface NotesDBSchema extends DBSchema {
  // 文档存储 - 存储所有类型的文档（含 UFS 同步运行时字段）
  documents: {
    key: string // 文档ID
    value: DocumentRecord // 文档数据 + 同步运行时字段
    indexes: {
      'by-type': DocumentType // 按文档类型索引
      'by-created-at': Date // 按创建时间索引
      'by-updated-at': Date // 按更新时间索引
      'by-title': string // 按标题索引（用于搜索）
      'by-tags': string // 按标签索引（用于标签搜索）
      'by-sync-status': SyncStatus // 按同步状态索引（用于查找待同步条目）
      'by-source': string // 按来源索引（native/plain，用于筛选 plain 文件）
      'by-ufs-parent-id': string // 按 UFS parent_id 索引（desk 列目录用）
      'by-parent-id': string // 按 parentId 索引（native 笔记层级，BlockPicker 目录浏览用）
    }
  }

  // 链接关系存储 - 存储文档间的关联关系
  links: {
    key: string // 链接ID
    value: DocumentLink // 链接数据
    indexes: {
      'by-source': string // 按源文档ID索引
      'by-target': string // 按目标文档ID索引
      'by-type': LinkType // 按链接类型索引
      'by-created-at': Date // 按创建时间索引
    }
  }

  // 标签存储 - 存储标签信息和使用统计
  tags: {
    key: string // 标签名称
    value: TagInfo // 标签信息
    indexes: {
      'by-count': number // 按使用次数索引
      'by-created-at': Date // 按创建时间索引
    }
  }

  // 搜索索引存储 - 存储全文搜索索引
  search_index: {
    key: string // 索引项ID (documentId + term)
    value: SearchIndexEntry // 搜索索引项
    indexes: {
      'by-document': string // 按文档ID索引
      'by-term': string // 按搜索词索引
      'by-score': number // 按相关性分数索引
    }
  }

  // 用户设置存储
  settings: {
    key: string // 设置键名
    value: UserSettings // 设置值
  }

  // 缓存存储 - 存储临时数据和缓存
  cache: {
    key: string // 缓存键
    value: CacheEntry // 缓存项
    indexes: {
      'by-expires-at': Date // 按过期时间索引
      'by-type': string // 按缓存类型索引
    }
  }

  // 文件附件存储 - 存储文件数据
  attachments: {
    key: string // 附件ID
    value: AttachmentData // 附件数据
    indexes: {
      'by-document': string // 按所属文档ID索引
      'by-type': string // 按文件类型索引
      'by-size': number // 按文件大小索引
      'by-created-at': Date // 按创建时间索引
    }
  }

  // 同步队列 - 暂存待推送到 UFS 的文档变更
  sync_queue: {
    key: string // 条目ID
    value: SyncQueueEntry
    indexes: {
      'by-doc-id': string // 按文档ID索引（查找某文档的待同步条目）
      'by-enqueued-at': Date // 按入队时间索引（FIFO 消费）
    }
  }

  // 同步元信息 - key-value 存储，记录全局同步状态
  sync_meta: {
    key: string
    value: SyncMeta
  }

  // 墓碑存储 - 记录已永久删除的 docId，防止 incrementalSync 重新拉回
  tombstones: {
    key: string // docId
    value: TombstoneRecord
  }
}

/**
 * 标签信息接口
 */
export interface TagInfo {
  name: string // 标签名称
  count: number // 使用次数
  color?: string // 标签颜色
  description?: string // 标签描述
  createdAt: Date // 创建时间
  updatedAt: Date // 更新时间
}

/**
 * 搜索索引项接口
 */
export interface SearchIndexEntry {
  id: string // 索引项ID
  documentId: string // 文档ID
  term: string // 搜索词
  score: number // 相关性分数
  positions: number[] // 词在文档中的位置
  field: string // 字段名称 (title, content, tags等)
  createdAt: Date // 创建时间
}

/**
 * 缓存项接口
 */
export interface CacheEntry {
  key: string // 缓存键
  value: unknown // 缓存值
  type: string // 缓存类型
  expiresAt: Date // 过期时间
  createdAt: Date // 创建时间
  size: number // 数据大小（字节）
}

/**
 * 附件数据接口
 */
export interface AttachmentData {
  id: string // 附件ID
  documentId: string // 所属文档ID
  name: string // 文件名
  type: string // MIME类型
  size: number // 文件大小（字节）
  data: ArrayBuffer // 文件数据
  thumbnail?: ArrayBuffer // 缩略图数据
  metadata: AttachmentMetadata // 附件元数据
  createdAt: Date // 创建时间
  updatedAt: Date // 更新时间
}

/**
 * 附件元数据接口
 */
export interface AttachmentMetadata {
  width?: number // 图片宽度
  height?: number // 图片高度
  duration?: number // 音视频时长
  encoding?: string // 编码格式
  checksum: string // 文件校验和
  [key: string]: unknown // 其他自定义元数据
}

/**
 * 数据库升级处理器类型
 */
export type UpgradeHandler = (
  db: IDBDatabase,
  oldVersion: number,
  newVersion: number,
  transaction: IDBTransaction
) => void

/**
 * 数据库升级处理器映射
 * 每个版本对应一个升级处理器
 */
export const upgradeHandlers: Record<number, UpgradeHandler> = {
  1: (db, oldVersion, newVersion, _transaction) => {
    logger.log(`数据库升级: v${oldVersion} -> v${newVersion}`)

    // 创建文档存储
    if (!db.objectStoreNames.contains('documents')) {
      const documentsStore = db.createObjectStore('documents', { keyPath: 'id' })

      // 创建索引
      documentsStore.createIndex('by-type', 'type', { unique: false })
      documentsStore.createIndex('by-created-at', 'createdAt', { unique: false })
      documentsStore.createIndex('by-updated-at', 'updatedAt', { unique: false })
      documentsStore.createIndex('by-title', 'title', { unique: false })
      documentsStore.createIndex('by-tags', 'tags', { unique: false, multiEntry: true })
      documentsStore.createIndex('by-sync-status', '_syncStatus', { unique: false })
      documentsStore.createIndex('by-source', 'source', { unique: false })

      logger.log('创建文档存储和索引')
    }

    // 创建链接存储
    if (!db.objectStoreNames.contains('links')) {
      const linksStore = db.createObjectStore('links', { keyPath: 'id' })

      // 创建索引
      linksStore.createIndex('by-source', 'sourceId', { unique: false })
      linksStore.createIndex('by-target', 'targetId', { unique: false })
      linksStore.createIndex('by-type', 'type', { unique: false })
      linksStore.createIndex('by-created-at', 'createdAt', { unique: false })

      logger.log('创建链接存储和索引')
    }

    // 创建标签存储
    if (!db.objectStoreNames.contains('tags')) {
      const tagsStore = db.createObjectStore('tags', { keyPath: 'name' })

      // 创建索引
      tagsStore.createIndex('by-count', 'count', { unique: false })
      tagsStore.createIndex('by-created-at', 'createdAt', { unique: false })

      logger.log('创建标签存储和索引')
    }

    // 创建搜索索引存储
    if (!db.objectStoreNames.contains('search_index')) {
      const searchStore = db.createObjectStore('search_index', { keyPath: 'id' })

      // 创建索引
      searchStore.createIndex('by-document', 'documentId', { unique: false })
      searchStore.createIndex('by-term', 'term', { unique: false })
      searchStore.createIndex('by-score', 'score', { unique: false })

      logger.log('创建搜索索引存储和索引')
    }

    // 创建设置存储
    if (!db.objectStoreNames.contains('settings')) {
      db.createObjectStore('settings', { keyPath: 'key' })
      logger.log('创建设置存储')
    }

    // 创建缓存存储
    if (!db.objectStoreNames.contains('cache')) {
      const cacheStore = db.createObjectStore('cache', { keyPath: 'key' })

      // 创建索引
      cacheStore.createIndex('by-expires-at', 'expiresAt', { unique: false })
      cacheStore.createIndex('by-type', 'type', { unique: false })

      logger.log('创建缓存存储和索引')
    }

    // 创建附件存储
    if (!db.objectStoreNames.contains('attachments')) {
      const attachmentsStore = db.createObjectStore('attachments', { keyPath: 'id' })

      // 创建索引
      attachmentsStore.createIndex('by-document', 'documentId', { unique: false })
      attachmentsStore.createIndex('by-type', 'type', { unique: false })
      attachmentsStore.createIndex('by-size', 'size', { unique: false })
      attachmentsStore.createIndex('by-created-at', 'createdAt', { unique: false })

      logger.log('创建附件存储和索引')
    }

    logger.log('数据库初始化完成')
  },

  // v2: UFS 同步支持 - 新增 sync_queue / sync_meta store，documents 补 by-sync-status 索引
  2: (db, oldVersion, newVersion, transaction) => {
    logger.log(`数据库升级: v${oldVersion} -> v${newVersion}`)

    // 老用户（v1 -> v2）的 documents store 已存在但缺 by-sync-status 索引，补建
    if (db.objectStoreNames.contains('documents')) {
      const documentsStore = transaction.objectStore('documents')
      if (!documentsStore.indexNames.contains('by-sync-status')) {
        documentsStore.createIndex('by-sync-status', '_syncStatus', { unique: false })
        logger.log('补建 documents.by-sync-status 索引')
      }
    }

    // 创建同步队列存储
    if (!db.objectStoreNames.contains('sync_queue')) {
      const syncQueueStore = db.createObjectStore('sync_queue', { keyPath: 'id' })
      syncQueueStore.createIndex('by-doc-id', 'docId', { unique: false })
      syncQueueStore.createIndex('by-enqueued-at', 'enqueuedAt', { unique: false })
      logger.log('创建 sync_queue 存储和索引')
    }

    // 创建同步元信息存储
    if (!db.objectStoreNames.contains('sync_meta')) {
      db.createObjectStore('sync_meta', { keyPath: 'key' })
      logger.log('创建 sync_meta 存储')
    }
  },

  // v3: tombstones store - 记录已 permanentDelete 的 docId，防止 incrementalSync 重新拉回
  3: (db, oldVersion, newVersion, _transaction) => {
    logger.log(`数据库升级: v${oldVersion} -> v${newVersion}`)

    if (!db.objectStoreNames.contains('tombstones')) {
      db.createObjectStore('tombstones', { keyPath: 'docId' })
      logger.log('创建 tombstones 存储')
    }
  },

  // v4: documents store 加 by-source 索引，支持按来源（native/plain）筛选
  4: (db, oldVersion, newVersion, transaction) => {
    logger.log(`数据库升级: v${oldVersion} -> v${newVersion}`)

    if (db.objectStoreNames.contains('documents')) {
      const documentsStore = transaction.objectStore('documents')
      if (!documentsStore.indexNames.contains('by-source')) {
        documentsStore.createIndex('by-source', 'source', { unique: false })
        logger.log('补建 documents.by-source 索引')
      }
    }
  },

  // v5: documents store 加 by-ufs-parent-id 索引，desk 列目录改读 IndexedDB
  //     旧记录 ufsParentId 为 undefined，下次 bootstrap 全量同步时 syncPlainFile 会补齐
  5: (db, oldVersion, newVersion, transaction) => {
    logger.log(`数据库升级: v${oldVersion} -> v${newVersion}`)

    if (db.objectStoreNames.contains('documents')) {
      const documentsStore = transaction.objectStore('documents')
      if (!documentsStore.indexNames.contains('by-ufs-parent-id')) {
        // ufsParentId 可能是 string 或 null，null 不进索引；用 undefined 兜底防止漏索引
        documentsStore.createIndex('by-ufs-parent-id', 'ufsParentId', { unique: false })
        logger.log('补建 documents.by-ufs-parent-id 索引')
      }
    }
  },

  // v6: documents store 加 by-parent-id 索引，BlockPicker native tab 目录浏览用
  //     native 笔记按 parentId 归档（与 plain 文件的 ufsParentId 语义不同）
  6: (db, oldVersion, newVersion, transaction) => {
    logger.log(`数据库升级: v${oldVersion} -> v${newVersion}`)

    if (db.objectStoreNames.contains('documents')) {
      const documentsStore = transaction.objectStore('documents')
      if (!documentsStore.indexNames.contains('by-parent-id')) {
        documentsStore.createIndex('by-parent-id', 'parentId', { unique: false })
        logger.log('补建 documents.by-parent-id 索引')
      }
    }
  },
}

/**
 * 获取数据库配置
 */
export const getDatabaseConfig = () => ({
  name: DATABASE_NAME,
  version: DATABASE_VERSION,
  upgradeHandlers,
})
